import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { PermissionGate } from '../../components/PermissionGate'
import { automationApi } from '../../api/automation.api'
import { toast } from '../../store/toastStore'
import { TRIGGER_LABELS, ACTION_LABELS } from './automationLabels'
import { AutomationRuleFormModal } from './AutomationRuleFormModal'
import { AutomationLogsPanel } from './AutomationLogsPanel'
import type { AutomationRule } from '../../api/types'

export function AutomationTab({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const [formRule, setFormRule] = useState<AutomationRule | null | undefined>(undefined)
  const [expandedLogsId, setExpandedLogsId] = useState<string | null>(null)

  const rulesQuery = useQuery({ queryKey: ['automation', projectId], queryFn: async () => (await automationApi.list(projectId)).data })

  const toggleMutation = useMutation({
    mutationFn: (rule: AutomationRule) => automationApi.update(rule.id, { enabled: !rule.enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['automation'], exact: false }),
    onError: () => toast.error('Konnte nicht aktualisiert werden.'),
  })

  const rules = rulesQuery.data ?? []

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
        <PermissionGate anyOf={['automation.manage']}>
          <Button variant="primary" size="sm" onClick={() => setFormRule(null)}>
            + Neue Automation
          </Button>
        </PermissionGate>
      </div>

      {rules.length === 0 ? (
        <EmptyState
          icon="⚡"
          title="Noch keine Automationen"
          description='Beispiel: WENN Label "Wichtig" gesetzt wird DANN sende eine Benachrichtigung.'
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {rules.map((rule) => (
            <Card key={rule.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <Badge variant={rule.enabled ? 'success' : 'neutral'} dot>
                  {rule.enabled ? 'Aktiv' : 'Deaktiviert'}
                </Badge>
                {!rule.projectId ? <Badge variant="info">Global</Badge> : null}
                <strong>{rule.name}</strong>
                <PermissionGate anyOf={['automation.manage']}>
                  <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                    <Button size="sm" variant="ghost" onClick={() => toggleMutation.mutate(rule)}>
                      {rule.enabled ? 'Deaktivieren' : 'Aktivieren'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setFormRule(rule)}>
                      Bearbeiten
                    </Button>
                  </div>
                </PermissionGate>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: '6px 0' }}>
                WENN <strong>{TRIGGER_LABELS[rule.triggerType]}</strong>
                {rule.conditions.length > 0 ? ` (${rule.conditions.length} Bedingung${rule.conditions.length > 1 ? 'en' : ''})` : ''} DANN{' '}
                {rule.actions.map((a) => ACTION_LABELS[a.type]).join(', ')}
              </p>
              <button
                onClick={() => setExpandedLogsId(expandedLogsId === rule.id ? null : rule.id)}
                style={{ background: 'none', border: 'none', color: 'var(--accent-10)', cursor: 'pointer', fontSize: 'var(--text-xs)', padding: 0 }}
              >
                {expandedLogsId === rule.id ? 'Logs ausblenden' : 'Logs anzeigen'}
              </button>
              {expandedLogsId === rule.id ? (
                <div style={{ marginTop: 8 }}>
                  <AutomationLogsPanel ruleId={rule.id} />
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}

      {formRule !== undefined ? <AutomationRuleFormModal projectId={projectId} rule={formRule} onClose={() => setFormRule(undefined)} /> : null}
    </div>
  )
}
